import { flash, SubmitButton } from "@xola/ui-kit";
import {
    isEquipmentBased,
    isScheduled,
    nullableNumber,
    requiredIntegerMoreThanZero,
    requiredNumberMoreThanZero,
} from "common";
import { FormikProps } from "formik/dist/types";
import { isNil, omit, pick } from "lodash";
import React, { useMemo, useRef } from "react";
import { useMutation } from "react-query";
import {
    AllowedPrivacy,
    CutoffType,
    DemographicPricingGroup,
    Experience,
    OpeningHour,
    PricingStructure,
    PurchaseRuleLink,
    PurchaseRuleLinkTag,
} from "types";
import { number, object } from "yup";
import { api } from "../../../api/api";
import { ScrollToFieldError } from "../../../helpers/validationHelper";
import { useCollection } from "../../../hooks/useCollection";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { generateDemographicPricingGroupFromPurchaseRules } from "../../../models/DemographicPricingGroup";
import { getPurchaseRulesFromPurchaseRuleLink } from "../../../models/PurchaseRuleLink";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";
import { isLimitAndNotifyGuideHidden } from "../pricing/ExperiencePricingFormHelper";
import { GuestLimitSelector } from "../pricing/GuestLimitSelector";
import { AvailabilityRestrictions } from "./AvailabilityRestrictions";
import { OpeningHoursSection } from "./OpeningHoursSection";
import { Schedules } from "./Schedules";
import { sanitizeExperiencePayload } from "@/models/Experience";

const SCHEDULES_AVAILABILITY_FIELDS: Array<keyof Experience> = [
    "duration",
    "eventDuration",
    "allowedPrivacies",
    "arrivalOffset",
    "cutoff",
    "alternateCutoff",
    "group",
];

const sectionClassNames = {
    content: "min-w-0 !max-w-full lg:w-0 lg:flex-1",
    title: "lg:!w-36 xl:!w-48",
};

type ExperienceFormValues = Experience & { openingHoursToDelete: OpeningHour[] };

export const ExperienceSchedulesAvailability = ({ experience }: { experience: Experience }) => {
    const invalidate = useInvalidate();
    const formikRef = useRef<FormikProps<ExperienceFormValues>>(null);

    const initialState: ExperienceFormValues = useMemo(() => {
        if (formikRef.current) {
            return {
                ...experience,
                ...pick(formikRef.current.values, SCHEDULES_AVAILABILITY_FIELDS),
                openingHoursToDelete: [],
            };
        }

        return { ...experience, openingHoursToDelete: [] };
    }, [experience]);

    const { data: purchaseRuleLinks } = useCollection<PurchaseRuleLink>("purchaseRuleLinks", {
        "tags._id": PurchaseRuleLinkTag.PRICING,
        "entities.id": experience?.id,
        expand: "purchaseRule",
        limit: 1,
    });

    const purchaseRuleLink = purchaseRuleLinks[0] ?? {};
    const purchaseRules = getPurchaseRulesFromPurchaseRuleLink(purchaseRuleLink);
    const privatePricingGroup = generateDemographicPricingGroupFromPurchaseRules(purchaseRules, AllowedPrivacy.PRIVATE);

    const { mutateAsync: save, isLoading: isSaving } = useMutation(async (payload: Partial<Experience>) => {
        return api.put(`/experiences/${experience?.id}`, sanitizeExperiencePayload(payload));
    });

    const handleSave = async (values: ExperienceFormValues) => {
        const deletable = values.openingHoursToDelete.filter((s) => !!s.id);
        if (deletable.length > 0) {
            await Promise.all(
                deletable.map(async (s) => api.delete(`/experiences/${experience.id}/openingHours/${s.id}`)),
            );
        }

        const savePayload = omit(values, "openingHoursToDelete");
        await save(savePayload);
        invalidate("experiences", experience?.id);
        invalidate("products", experience?.id);
        invalidate("timeslots");
        flash.success("Schedule & availability saved");
    };

    const validationSchema = object().shape({
        cutoff: nullableNumber().typeError("must be a number").moreThan(0),
        alternateCutoff: nullableNumber().typeError("must be a number").moreThan(0),
        arrivalOffset: nullableNumber().typeError("must be a number").moreThan(0),
        eventDuration: durationValidator(),
        duration: durationValidator(),
        futureCutoff: object()
            .nullable()
            .shape({
                value: nullableNumber().typeError("must be a number").moreThan(0),
            }),
        group: groupValidator(initialState.allowedPrivacies, privatePricingGroup),
    });

    return (
        <ContentLayout isWide>
            <XolaFormik<ExperienceFormValues>
                enableReinitialize
                initialValues={initialState}
                validationSchema={validationSchema}
                innerRef={formikRef}
                onSubmit={handleSave}
            >
                {({ values, setValues, errors, handleSubmit, setFieldValue, handleReset, isValid }) => {
                    return (
                        <>
                            <div className="flex flex-col space-y-8">
                                <h2>Schedules & Availability</h2>
                                <div className="mb-10 divide-y divide-gray-light">
                                    <ContentLayout.Section
                                        classNames={sectionClassNames}
                                        title="Schedules"
                                        description="Build activity schedules that your Experiences is available to be booked by customers."
                                    >
                                        <Schedules
                                            values={values}
                                            errors={errors}
                                            onChange={(newValues) =>
                                                setValues({
                                                    ...newValues,
                                                    openingHoursToDelete: values.openingHoursToDelete,
                                                })
                                            }
                                        />
                                    </ContentLayout.Section>

                                    {!isEquipmentBased(experience) && (
                                        <ContentLayout.Section
                                            title="Guest Limits"
                                            description="Set min and max limits to how many guests may book."
                                            classNames={sectionClassNames}
                                        >
                                            <GuestLimitSelector
                                                experience={{ ...values, privatePricing: privatePricingGroup }}
                                                errors={errors?.group}
                                                onChange={async (value) => setFieldValue("group", value)}
                                            />
                                        </ContentLayout.Section>
                                    )}

                                    <ContentLayout.Section title="Business Hours" classNames={sectionClassNames}>
                                        <OpeningHoursSection
                                            experience={experience}
                                            pendingDisable={values.openingHoursToDelete.length > 0}
                                            requiredByEarlyCutoff={values.cutoffType === CutoffType.PRE_OPENING_END}
                                            onPendingDisableChange={(disabled, schedules) => {
                                                setFieldValue("openingHoursToDelete", disabled ? schedules ?? [] : []);
                                            }}
                                        />
                                    </ContentLayout.Section>

                                    {isScheduled(values) && (
                                        <ContentLayout.Section
                                            title="Availability Restrictions"
                                            description="Set limits to when your experiences can be booked."
                                            classNames={sectionClassNames}
                                        >
                                            <AvailabilityRestrictions
                                                values={values}
                                                errors={errors}
                                                onChange={(newValues) =>
                                                    setValues({
                                                        ...newValues,
                                                        openingHoursToDelete: values.openingHoursToDelete,
                                                    })
                                                }
                                            />
                                        </ContentLayout.Section>
                                    )}
                                </div>
                            </div>
                            <ContentLayout.Footer preferenceSidebar>
                                <SubmitButton disabled={!isValid} isLoading={isSaving} onClick={handleSubmit}>
                                    Save
                                </SubmitButton>
                                <SecondaryButton type="reset" onClick={handleReset}>
                                    Cancel
                                </SecondaryButton>
                            </ContentLayout.Footer>

                            <ScrollToFieldError />
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

const maxValueValidator = (minValueName: "orderMin" | "outingMin", isBuyoutPricing = false) =>
    nullableNumber()
        .integer("Should be valid number")
        .typeError("Should be valid number")
        .test(
            "isBuyoutPricing",
            "Event limit must be configured as this experience uses buyout remaining spots pricing for private reservations",
            (value) => {
                return !isBuyoutPricing || !!value;
            },
        )
        .test("minValue", "Must be greater than minimum", (value, context) => {
            const { [minValueName]: minValue } = context.parent;
            return isNil(value) || (!isNil(value) && minValue && value >= minValue);
        });

const durationValidator = () =>
    number().when(["scheduleType", "isScheduled"], (scheduleType, isScheduledField) => {
        return isScheduled({ scheduleType, isScheduled: isScheduledField } as Experience)
            ? requiredNumberMoreThanZero()
            : nullableNumber();
    });

const groupValidator = (
    allowedPrivacies: AllowedPrivacy[] | undefined,
    privatePricingGroup: DemographicPricingGroup | undefined,
) =>
    object().when(["scheduleType", "isScheduled"], (scheduleType, isScheduledField) => {
        const isHidden = isLimitAndNotifyGuideHidden(allowedPrivacies, privatePricingGroup);
        const isBuyoutPricing = privatePricingGroup?.pricingStructure === PricingStructure.BUYOUT;
        if (isScheduled({ scheduleType, isScheduled: isScheduledField } as Experience)) {
            return isHidden
                ? object().shape({ outingMax: maxValueValidator("outingMin", isBuyoutPricing) })
                : object().shape({
                      outingMax: maxValueValidator("outingMin", isBuyoutPricing),
                      orderMin: requiredIntegerMoreThanZero().min(1, "Must be at least 1"),
                      orderMax: maxValueValidator("orderMin").when(["outingMax"], (outingMax, schema) => {
                          return outingMax
                              ? schema.max(outingMax, "Per booking maximum must be lesser than the per event maximum")
                              : schema;
                      }),
                      outingMin: requiredIntegerMoreThanZero().min(1, "Should be valid number"),
                      outingMinCutoff: requiredIntegerMoreThanZero()
                          .min(60, "must be at-least 60")
                          .test("valid number", "Should be a number in multiples of 60 (60, 120, 180 etc)", (value) => {
                              return value ? Number.isInteger(value / 60) : false;
                          }),
                  });
        }

        return isHidden
            ? object().nullable().shape({})
            : object().shape({
                  orderMin: requiredIntegerMoreThanZero().min(1, "Must be at least 1"),
              });
    });
